import { useQuery } from '@tanstack/react-query';

import { authedJson } from '@/lib/http';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDateCompact } from '@/lib/dateUtils';

const asArray = <T,>(v: unknown): T[] =>
  Array.isArray(v) ? (v as T[]) : (((v as { data?: T[] } | null)?.data ?? []) as T[]);

const usd = (c: number) => `${c < 0 ? '-' : '+'}$${Math.abs(c / 100).toFixed(2)}`;

// ── Ledger ──────────────────────────────────────────────────────────────────
interface LedgerRow {
  id: string;
  creatorDisplayName: string | null;
  payeeType: string;
  payeeId: string | null;
  entryType: string;
  amountCents: number;
  orderId: string | null;
  referenceNote: string | null;
  createdAt: string;
  purchaserName: string | null;
  purchaserEmail: string | null;
  itemTitle: string | null;
  sessionStartDateTime: string | null;
  quantity: number | null;
  orderGrossAmountCents: number | null;
}

export function LedgerAdmin() {
  const { data, isLoading } = useQuery<unknown>({
    queryKey: ['/api/admin/financials/ledger'],
    queryFn: () => authedJson('/api/admin/financials/ledger?limit=50'),
  });
  const rows = asArray<LedgerRow>(data);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Platform ledger</CardTitle>
        <CardDescription>Most recent 50 payout ledger entries across all payees.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No ledger entries.</p>
        ) : (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Payee</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Purchaser</th>
                  <th className="py-2 pr-4 font-medium">Item</th>
                  <th className="py-2 pr-4 text-right font-medium">Qty</th>
                  <th className="py-2 pr-4 font-medium">Reference</th>
                  <th className="py-2 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                      {formatDateCompact(r.createdAt)}
                    </td>
                    <td className="py-2 pr-4">
                      {r.creatorDisplayName || `${r.payeeType} (${(r.payeeId ?? '').slice(0, 8)})`}
                    </td>
                    <td className="py-2 pr-4">
                      <Badge variant="outline" className="text-xs capitalize">
                        {r.entryType.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4 max-w-[180px]">
                      {r.purchaserName ? (
                        <div className="min-w-0">
                          <p className="truncate font-medium">{r.purchaserName}</p>
                          {r.purchaserEmail && (
                            <p className="truncate text-xs text-muted-foreground">
                              {r.purchaserEmail}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 max-w-[200px]">
                      {r.itemTitle ? (
                        <div className="min-w-0">
                          <p className="truncate">{r.itemTitle}</p>
                          {r.sessionStartDateTime && (
                            <p className="truncate text-xs text-muted-foreground">
                              {formatDateCompact(r.sessionStartDateTime)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">
                      {r.quantity ?? '—'}
                    </td>
                    <td className="py-2 pr-4 max-w-[160px] truncate text-muted-foreground">
                      {r.referenceNote ?? r.orderId ?? '—'}
                    </td>
                    <td className="py-2 text-right">
                      <p
                        className={`tabular-nums font-medium ${r.amountCents < 0 ? 'text-destructive' : 'text-foreground'}`}
                      >
                        {usd(r.amountCents)}
                      </p>
                      {typeof r.orderGrossAmountCents === 'number' &&
                        r.orderGrossAmountCents !== Math.abs(r.amountCents) && (
                          <p className="text-xs text-muted-foreground tabular-nums">
                            of ${(r.orderGrossAmountCents / 100).toFixed(2)} gross
                          </p>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Connected accounts ──────────────────────────────────────────────────────
interface AccountRow {
  id: string;
  creatorDisplayName: string | null;
  payeeType: string;
  payeeId: string | null;
  stripeAccountId: string;
  onboardingStatus: string;
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
}

export function ConnectedAccountsAdmin() {
  const { data, isLoading } = useQuery<unknown>({
    queryKey: ['/api/admin/financials/connected-accounts'],
    queryFn: () => authedJson('/api/admin/financials/connected-accounts'),
  });
  const rows = asArray<AccountRow>(data);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Connected accounts</CardTitle>
        <CardDescription>
          Stripe payout readiness for creators, venues, and ambassadors.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No connected accounts yet.</p>
        ) : (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Payee</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Onboarding</th>
                  <th className="py-2 font-medium">Payouts</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(a => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">
                      {a.creatorDisplayName || `(${(a.payeeId ?? '').slice(0, 8)})`}
                    </td>
                    <td className="py-2 pr-4 capitalize">{a.payeeType.replace(/_/g, ' ')}</td>
                    <td className="py-2 pr-4 capitalize text-muted-foreground">
                      {a.onboardingStatus.replace(/_/g, ' ')}
                    </td>
                    <td className="py-2">
                      <Badge variant={a.payoutsEnabled ? 'default' : 'destructive'}>
                        {a.payoutsEnabled ? 'Enabled' : 'Not ready'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Audit log ───────────────────────────────────────────────────────────────
interface AuditRow {
  id: string;
  actorId: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export function AuditLogAdmin() {
  const { data, isLoading } = useQuery<unknown>({
    queryKey: ['/api/admin/audit-logs'],
    queryFn: () => authedJson('/api/admin/audit-logs?limit=100'),
  });
  const rows = asArray<AuditRow>(data);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Audit log</CardTitle>
        <CardDescription>Most recent 100 admin actions.</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No audit entries.</p>
        ) : (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Action</th>
                  <th className="py-2 pr-4 font-medium">Target</th>
                  <th className="py-2 pr-4 font-medium">Actor</th>
                  <th className="py-2 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                      {formatDateCompact(r.createdAt)}
                    </td>
                    <td className="py-2 pr-4 font-medium">{r.action}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {r.targetType ? `${r.targetType}:${(r.targetId ?? '').slice(0, 8)}` : '—'}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {(r.actorId ?? '—').slice(0, 8)}
                    </td>
                    <td className="py-2 text-muted-foreground">{r.ipAddress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
